import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';

import BlIcon from 'components/icons/BlIcon';
import { switchProductType } from 'helpers/campaign';
import { useTimeframeDateStrings, useTimeframeLS } from 'hooks/useTimeframeLS';
import QueryString from 'qs';
import s from './styled';

const { REACT_APP_API_URI } = process.env;

const ActionsPanel = memo(function ActionsPanel(props) {
  const {
    className,
    disabled,
    selectionIds,
    productType,
    onDuplicate,
    onActivate,
    onPause,
    onExport,
    onImport,
    onEdit,
    onDelete,
    onArchive,
    onUnarchive,
    DuplicateProps,
    ActivateProps,
    PauseProps,
    ExportProps,
    ImportProps,
    isGroups,
    EditProps,
    DeleteProps,
    ArchiveProps,
    UnarchiveProps,
  } = props;

  const { timeframe } = useTimeframeLS();
  const timeframeDates = useTimeframeDateStrings(timeframe);

  let exportParams = {
    product: switchProductType(productType),
    ...(ExportProps?.params && { ...ExportProps?.params }),
    ...timeframeDates,
  };

  if (!!selectionIds?.length) {
    exportParams = {
      ...exportParams,
      ...(isGroups && { group: selectionIds.join(',') }),
      ...(!isGroups && { buy_campaign_ids: selectionIds.join(',') }),
    };
  }

  return (
    <s.ActionsWrapper className={className}>
      {[
        {
          tooltip: 'Restore',
          icon: 'unarchive',
          onClick: onUnarchive,
          disabled,
          ...UnarchiveProps,
        },
        {
          tooltip: 'Activate',
          icon: 'play',
          onClick: onActivate,
          active: true,
          disabled,
          ...ActivateProps,
        },
        {
          tooltip: 'Pause',
          icon: 'pause',
          onClick: onPause,
          disabled,
          ...PauseProps,
        },
        {
          tooltip: 'Export',
          icon: 'export',
          onClick: onExport,
          target: '_blank',
          component: 'a',
          href: `${REACT_APP_API_URI}BuyCampaignExport/${QueryString.stringify(
            exportParams,
            { addQueryPrefix: true },
          )}`,
          disabled,
          ...ExportProps,
        },
        {
          tooltip: 'Import',
          icon: 'import',
          onClick: onImport,
          disabled,
          ...ImportProps,
        },
        {
          tooltip: 'Duplicate',
          icon: 'duplicate',
          onClick: onDuplicate,
          disabled: disabled || selectionIds?.length !== 1,
          hide: false,
          ...DuplicateProps,
        },
        {
          tooltip: 'Edit',
          icon: 'edit',
          onClick: onEdit,
          hide: false,
          disabled: disabled || selectionIds?.length !== 1,
          ...EditProps,
        },
        {
          tooltip: 'Archive',
          icon: 'archive',
          onClick: onArchive,
          disabled,
          ...ArchiveProps,
        },
        {
          tooltip: 'Delete',
          icon: 'delete',
          onClick: onDelete,
          disabled,
          ...DeleteProps,
        },
      ].map(({ icon, tooltip, active, hide, ...item }) => {
        return (
          <Tooltip title={tooltip} key={icon + tooltip}>
            <span>
              <s.ActionItem
                $active={active}
                $hide={hide}
                {...item}
                size="medium"
                data-testid={`${icon}-button`}
              >
                <BlIcon icon={icon} />
              </s.ActionItem>
            </span>
          </Tooltip>
        );
      })}
    </s.ActionsWrapper>
  );
});

ActionsPanel.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  productType: PropTypes.string,
  selectionIds: PropTypes.array,
  onDuplicate: PropTypes.func,
  onActivate: PropTypes.func,
  onPause: PropTypes.func,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  onEdit: PropTypes.func,
  onArchive: PropTypes.func,
  onUnarchive: PropTypes.func,
  onDelete: PropTypes.func,
  DuplicateProps: PropTypes.object,
  ActivateProps: PropTypes.object,
  PauseProps: PropTypes.object,
  ExportProps: PropTypes.object,
  ImportProps: PropTypes.object,
  EditProps: PropTypes.object,
  ArchiveProps: PropTypes.object,
  DeleteProps: PropTypes.object,
  UnarchiveProps: PropTypes.object,
  isGroups: PropTypes.bool,
};

export default ActionsPanel;
